/**
 * @description
 * Este arquivo cria diferentes instâncias do axios para cada um dos backends utilizados pelo painel.
 * Este arquivo também define os endpoints (rotas) dos diferentes backends utilizados pelo painel.
 *
 * As rotas descritas no arquivo `src/routes/paths.ts` são referentes aos "caminhos de URL" do frontend
 * (paths), que serão utilizados em outros arquivos para navegação via react-router.
 */

import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';

import { APP_API_URL } from 'src/config-global';

// Factory function para criar instâncias axios padronizadas
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    (config) => {
      // Se for FormData, não sobrescreva o Content-Type
      // O browser vai setar automaticamente com o boundary correto
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
      return { ...config };
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (res) => res,
    (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
  );

  return instance;
};

export type axiosInstanceType = 'axiosInstance';

// Criar as instâncias
export const axiosInstance = createAxiosInstance(APP_API_URL);

// Factory function para criar fetchers
const createFetcher = (instance: AxiosInstance) => async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await instance.get(url, { ...config });
  return res.data;
};

// Criar os fetchers
export const appFetcher = createFetcher(axiosInstance);

// Endpoints da App API
export const endpoints = {
  webapp: {
    auth: {
      login: '/webapp/auth/login',
      refresh: '/webapp/auth/refresh',
      logout: '/webapp/auth/logout',
    },
    appUsers: {
      wallet: (walletId: string) => `/webapp/app-users/wallet/${walletId}`,
    },
    points: {
      give: '/webapp/points/give',
    },
    benefits: {
      redeems: (walletId: string) => `/webapp/info/${walletId}/redeems`,
      withdraw: '/webapp/withdraw',
    },
    campaigns: {
      redeems: (walletId: string) => `/webapp/info/${walletId}/campaign-rewards`,
      withdraw: '/webapp/campaign-rewards/withdraw',
    },
    whitelabel: '/admin/whitelabel',
  },
};
